import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  type VisibilityState,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Unit } from "@/lib/api";
import { statusBadgeProps, kwhFromSessionEnergy, formatNum } from "@/lib/unit-helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { useAuth } from "@/app/AuthContext";

const col = createColumnHelper<Unit>();

const columns = [
  col.accessor("state_name", {
    id: "status",
    header: "Status",
    cell: (info) => {
      const u = info.row.original;
      const online = u.is_online !== false;
      const bp = statusBadgeProps(info.getValue() || "", online);
      return (
        <Badge variant={bp.variant} outline={bp.outline}>
          {online ? info.getValue() || "unknown" : "offline"}
        </Badge>
      );
    },
    size: 100,
  }),
  col.accessor("name", {
    id: "name",
    header: "Unit",
    cell: (info) => {
      const u = info.row.original;
      const raw = info.getValue() || "";
      const hasCustomName = raw && raw !== `Unit ${u.unit_id}`;
      return (
        <div className="overflow-hidden">
          {hasCustomName && <div className="font-semibold truncate">{raw}</div>}
          <div className="font-mono text-xs overflow-hidden text-ellipsis text-left opacity-60" dir="rtl">{u.unit_id}</div>
        </div>
      );
    },
    size: 220,
  }),
  col.accessor("unit_id", {
    id: "unit_id_col",
    header: "Unit ID",
    cell: (info) => <span className="font-mono text-sm overflow-hidden text-ellipsis text-left block" dir="rtl">{info.getValue()}</span>,
    size: 180,
  }),
  col.display({
    id: "product",
    header: "Product",
    cell: (info) => (
      <span className="text-sm">{(info.row.original as any).product_class_short || "Unknown"}</span>
    ),
    size: 120,
  }),
  col.accessor("voltage", {
    id: "voltage",
    header: "Voltage",
    cell: (info) => <>{formatNum(info.getValue(), 1)}V</>,
    size: 90,
  }),
  col.accessor("current", {
    id: "current",
    header: "Current",
    cell: (info) => <>{formatNum(info.getValue(), 1)}A</>,
    size: 90,
  }),
  col.accessor("session_energy", {
    id: "session_energy",
    header: "Session Energy",
    cell: (info) => <span className="font-semibold">{kwhFromSessionEnergy(info.getValue() ?? 0)} kWh</span>,
    size: 130,
  }),
  col.accessor("power_factor", {
    id: "power_factor",
    header: "PF",
    cell: (info) => <>{formatNum(info.getValue(), 2)}</>,
    size: 70,
  }),
  col.accessor("protocol_version", {
    id: "protocol_version",
    header: "Protocol",
    cell: (info) => <span className="text-sm">{info.getValue() ?? "—"}</span>,
    size: 90,
  }),
  col.display({
    id: "location",
    header: "Location",
    cell: (info) => (
      <span className="text-sm max-w-[180px] truncate block">
        {(info.row.original as any).location || "Unknown"}
      </span>
    ),
    size: 180,
  }),
  col.display({
    id: "actions",
    header: () => <span className="text-right block">Actions</span>,
    cell: (info) => (
      <div className="text-right">
        <Link
          className="text-primary underline-offset-4 hover:underline"
          to={`/unit/${encodeURIComponent(info.row.original.unit_id)}`}
          onClick={(e) => e.stopPropagation()}
        >
          Details →
        </Link>
      </div>
    ),
    size: 90,
  }),
];

interface ChargerTableProps {
  units: Unit[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  onRowClick: (unit: Unit) => void;
  /** Double-click a row to open the full charger details page. */
  onRowDoubleClick?: (unit: Unit) => void;
  selectedUnitId?: string;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (v: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void;
  sorting: SortingState;
  onSortingChange: (s: SortingState | ((prev: SortingState) => SortingState)) => void;
}

export function ChargerTable({
  units,
  total,
  hasMore,
  isLoading,
  onLoadMore,
  onRowClick,
  onRowDoubleClick,
  selectedUnitId,
  columnVisibility,
  onColumnVisibilityChange,
  sorting,
  onSortingChange,
}: ChargerTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isAdmin } = useAuth();
  // Protocol version is an admin-only diagnostic — hide the column (and its
  // ColumnChooser entry) for regular users.
  const visibleColumns = useMemo(
    () => (isAdmin ? columns : columns.filter((c) => c.id !== "protocol_version")),
    [isAdmin],
  );

  const table = useReactTable({
    data: units,
    columns: visibleColumns,
    state: { columnVisibility, sorting },
    onColumnVisibilityChange: onColumnVisibilityChange as any,
    onSortingChange: onSortingChange as any,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.unit_id,
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  return (
    <DataTableShell
      ref={scrollRef}
      footer={
        <>
          <div className="text-sm opacity-70">
            Showing <span className="font-semibold">{units.length}</span> of{" "}
            <span className="font-semibold">{total}</span>
          </div>
          {hasMore ? (
            <Button variant="outline" size="sm" disabled={isLoading} onClick={onLoadMore}>
              {isLoading ? "Loading..." : "Load More"}
            </Button>
          ) : (
            <div className="text-sm opacity-60">All loaded</div>
          )}
        </>
      }
    >
      <Table className="table-fixed min-w-[1200px]">
        <TableHeader className="sticky top-0 bg-card z-10 shadow-[0_1px_0_var(--color-border)]">
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className="cursor-pointer select-none"
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() === "asc" && " \u25B2"}
                  {header.column.getIsSorted() === "desc" && " \u25BC"}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 && isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                {table.getVisibleLeafColumns().map((col) => (
                  <TableCell key={col.id}>
                    <Skeleton className="h-4 w-full rounded-md" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={table.getVisibleLeafColumns().length} className="text-center py-10 opacity-70">
                No units found
              </TableCell>
            </TableRow>
          ) : (
            <>
              {/* spacer top */}
              {virtualizer.getVirtualItems()[0]?.start > 0 && (
                <TableRow>
                  <TableCell style={{ height: virtualizer.getVirtualItems()[0].start, padding: 0 }} colSpan={table.getVisibleLeafColumns().length} />
                </TableRow>
              )}
              {virtualizer.getVirtualItems().map((vRow) => {
                const row = rows[vRow.index];
                const isSelected = row.original.unit_id === selectedUnitId;
                return (
                  <TableRow
                    key={row.id}
                    data-index={vRow.index}
                    ref={virtualizer.measureElement}
                    className={[
                      "cursor-pointer hover:bg-muted/60 transition-colors",
                      onRowDoubleClick ? "select-none" : "",
                      isSelected ? "bg-primary/10" : "",
                      row.original.is_online === false ? "opacity-70" : "",
                    ].join(" ")}
                    onClick={() => onRowClick(row.original)}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {/* spacer bottom */}
              {virtualizer.getVirtualItems().length > 0 && (
                <TableRow>
                  <TableCell
                    style={{
                      height:
                        virtualizer.getTotalSize() -
                        (virtualizer.getVirtualItems().at(-1)?.end ?? 0),
                      padding: 0,
                    }}
                    colSpan={table.getVisibleLeafColumns().length}
                  />
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </DataTableShell>
  );
}
